import * as H from 'history'
import React from 'react'

import { BaseTheWest } from 'component-library/templates/BaseTheWest/BaseTheWest'
import { NavEvent } from 'common/events/nav-event-types'

import { StyledSite } from 'thewest/Site.styled'

import { ScrollToTopOnNav } from 'component-library/routing/scroll-on-nav'
import { AdState } from 'common/advertising/ad-state'

import { layout } from 'thewest/App.routing'
import { DebugAdUnitsList } from 'component-library/advertising/AdUnit/DebugAdUnitsList'
import { FlyoutNavStateProvider } from 'component-library/navigation/FlyoutNav/FlyoutNav'
import { UserAnalytics } from 'component-library/user-registration/user-analytics/user-analytics'
import { UserAuthEvents } from 'common/events/user-event-types'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { TheWestSection } from 'component-library/__styling/settings/sections/thewest'
import { Product } from 'component-library/__product'
import { AdDefinition } from 'common/advertising/add-ads'
import { AdEvent } from 'common/events/viewability-event'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'
import { SubscribeEvent } from 'common/events/subscribeEventTypes'
import { CookieConsentManager } from 'component-library/cookie-consent/CookieConsentManager'
import { CookieConsentEvent } from 'common/events/cookie-consent'
import { TheWestLiveProvider } from 'component-library/content/TheWestLive/TheWestLiveProvider'

export interface SiteProps {
    pageContents: React.ReactElement<any>
    pageType: string
    additionalPageProperties: object
    location: H.Location
    onEvent: (
        event: NavEvent | AdEvent | UserAuthEvents | SubscribeEvent | CookieConsentEvent
    ) => void
    config: BaseClientConfig
    ads: AdState | undefined
    section: TheWestSection
}

export interface State {
    mobileNavExpanded: boolean
}

const headerUnit: AdDefinition = {
    id: 'header',
    size: 'leaderboard768Above',
    isSitewideAd: true,
}

const stickyFooterUnit: AdDefinition = {
    id: 'sticky-footer',
    size: 'mobileLeaderboard',
}

export const siteAds: AdDefinition[] = [headerUnit, stickyFooterUnit]

// This emulates the ads rendered above the content in this file for the ad-diagnostics page
export const headerCompositions: typeof layout._compositionType[] = [
    {
        type: 'thor',
        props: {},
        contentAreas: {
            main: [
                {
                    type: 'ad-unit',
                    props: {
                        noticePosition: 'top-right',
                        slot: headerUnit,
                    },
                },
            ],
        },
    },
]

export const footerCompositions: typeof layout._compositionType[] = [
    {
        type: 'thor',
        props: {},
        contentAreas: {
            main: [
                {
                    type: 'ad-unit',
                    props: {
                        noticePosition: 'none',
                        slot: stickyFooterUnit,
                    },
                },
            ],
        },
    },
]

export const Site: React.FC<SiteProps> = ({
    config,
    location,
    ads,
    section,
    onEvent,
    pageContents,
    additionalPageProperties,
    pageType,
}) => {
    const baseWest = (
        <FlyoutNavStateProvider>
            {flyOutProps => (
                <BaseTheWest
                    additionalPageProperties={additionalPageProperties}
                    ads={ads}
                    config={config}
                    flyOutProps={flyOutProps}
                    location={location}
                    onEvent={onEvent}
                    pageContents={pageContents}
                    pageType={pageType}
                    product={Product.TheWest}
                    section={section}
                />
            )}
        </FlyoutNavStateProvider>
    )

    return (
        <React.Fragment>
            <UserAnalytics />
            <StyledSite>
                <ScrollToTopOnNav />

                <FeatureToggle
                    feature="the-west-live"
                    on={() => (
                        <TheWestLiveProvider
                            stationId={config.theWestLiveStationId}
                            startTime={[0, 30]}
                            endTime={[1, 30]}
                        >
                            {baseWest}
                        </TheWestLiveProvider>
                    )}
                    off={() => baseWest}
                />
                <FeatureToggle
                    feature="debug-ads"
                    on={() => <DebugAdUnitsList ads={ads} pathname={location.pathname} />}
                />
            </StyledSite>
            <FeatureToggle
                feature="cookie-consent"
                on={() => <CookieConsentManager onEvent={onEvent} />}
            />
        </React.Fragment>
    )
}
Site.displayName = 'Site'
